import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import DescriptionIcon from "@mui/icons-material/Description";
import type { RecentRender } from "@/api/dashboard";
import ResponsiveTableContainer from "@/components/ResponsiveTableContainer";

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  completed: "success",
  processing: "info",
  queued: "warning",
  failed: "error",
  canceled: "default",
  expired: "default",
};

const allColumns: GridColDef[] = [
  {
    field: "id",
    headerName: "Job ID",
    width: 180,
    renderCell: (params) => (
      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
        {params.value}
      </Typography>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={statusColors[params.value as string] ?? "default"}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    field: "inputType",
    headerName: "Input",
    width: 80,
    renderCell: (params) => (
      <Chip label={params.value} size="small" variant="filled" />
    ),
  },
  {
    field: "pageCount",
    headerName: "Pages",
    width: 80,
    type: "number",
    renderCell: (params) => params.value ?? "-",
  },
  {
    field: "fileSize",
    headerName: "Size",
    width: 100,
    renderCell: (params) => formatFileSize(params.value as number | null),
  },
  {
    field: "renderTimeMs",
    headerName: "Time",
    width: 90,
    renderCell: (params) => formatDuration(params.value as number | null),
  },
  {
    field: "isTest",
    headerName: "Test",
    width: 70,
    renderCell: (params) =>
      params.value ? (
        <Chip label="Test" size="small" color="secondary" variant="outlined" />
      ) : null,
  },
  {
    field: "createdAt",
    headerName: "Created",
    width: 180,
    renderCell: (params) => new Date(params.value as string).toLocaleString(),
  },
  {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const token = params.row.downloadToken as string | null;
      if (!token) {
        return (
          <IconButton size="small" disabled aria-label="download unavailable">
            <DescriptionIcon fontSize="small" />
          </IconButton>
        );
      }
      return (
        <Tooltip title="View PDF">
          <IconButton
            size="small"
            component="a"
            href={`/dl/${token}/inline`}
            target="_blank"
            aria-label="view PDF"
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

interface RecentRendersTableProps {
  rows: RecentRender[];
  title?: string;
}

export default function RecentRendersTable({
  rows,
  title = "Recent Renders",
}: RecentRendersTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileColumnFields = new Set([
    "status",
    "pageCount",
    "createdAt",
    "actions",
  ]);
  const columns = isMobile
    ? allColumns.filter((col) => mobileColumnFields.has(col.field))
    : allColumns;

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          mb={1}
        >
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <Typography
            variant="body2"
            component={Link}
            to="/dashboard/logs"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all →
          </Typography>
        </Box>
        <ResponsiveTableContainer>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 5 } },
            }}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: "none",
              minWidth: isMobile ? 480 : 800,
            }}
          />
        </ResponsiveTableContainer>
      </CardContent>
    </Card>
  );
}
